import {
  useGetDownloadSignedUrlForExamCourseCommentLazyQuery,
  useGetDownloadSignedUrlForQuestionLazyQuery,
} from '@exam-management/graphql';
import { useCallback } from 'react';

export function parseContentForApi(content: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');

  const imgElements = doc.getElementsByTagName('img');

  for (const img of Array.from(imgElements)) {
    // Only process images that have both src and content attributes
    if (img.getAttribute('content') && img.hasAttribute('src')) {
      img.removeAttribute('src');
    }
  }

  // Return the processed HTML string
  return doc.body.innerHTML;
}

// Function to extract all filenames from the HTML content
export function extractImagesFileNames(content: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.getElementsByTagName('img');

  const filenames: string[] = [];

  for (const img of Array.from(images)) {
    const contentAttr = img.getAttribute('content');
    if (contentAttr) {
      filenames.push(contentAttr);
    }
  }

  return [...new Set(filenames)]; // Remove duplicates
}

// Function to update the src attribute of img tags with the provided URL object
export function updateImageTags(content: string, urlObject: Record<string, string>) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.getElementsByTagName('img');

  for (const img of Array.from(images)) {
    const contentAttr = img.getAttribute('content');
    if (contentAttr && urlObject[contentAttr]) {
      img.setAttribute('src', urlObject[contentAttr]);
    }
  }

  return doc.body.innerHTML;
}

export interface UseParseContentForQuestionInput {
  questionId: string;
}

export interface UseParseContentForItemSetInput {
  questionGroupId: string;
}

export interface UseParseContentForDisplayQSetReviewInput {
  taskId: string;
}

type UseParseContentForDisplayInput =
  | UseParseContentForQuestionInput
  | UseParseContentForItemSetInput;

export function useParseContentForDisplay(params: UseParseContentForDisplayInput) {
  const questionGroupId = 'questionGroupId' in params ? params.questionGroupId : undefined;
  const questionId = 'questionId' in params ? params.questionId : undefined;
  const [getDownloadSignedUrlForQuestion] = useGetDownloadSignedUrlForQuestionLazyQuery();

  const parseContentForDisplay = useCallback(
    async (content: string) => {
      const fileNames = extractImagesFileNames(content);

      if (fileNames.length === 0) {
        return content;
      }

      const { data } = await getDownloadSignedUrlForQuestion({
        variables: {
          input: { fileNames, questionId, questionGroupId },
        },
      });

      const urlObject: Record<string, string> | undefined =
        data?.qctGetDownloadSignedUrlQuestion?.signedUrls;

      if (!urlObject) {
        return content;
      }

      return updateImageTags(content, urlObject);
    },
    [questionId, questionGroupId, getDownloadSignedUrlForQuestion],
  );

  return { parseContentForDisplay };
}

export function useParseContentForDisplayQSetReview(
  params: UseParseContentForDisplayQSetReviewInput,
) {
  const { taskId } = params;

  const [getDownloadSignedUrlForQuestion] = useGetDownloadSignedUrlForExamCourseCommentLazyQuery();

  const parseContentForDisplay = useCallback(
    async (content: string) => {
      const fileNames = extractImagesFileNames(content);

      if (fileNames.length === 0) {
        return content;
      }

      const { data } = await getDownloadSignedUrlForQuestion({
        variables: {
          getDownloadSignedUrlForExamCourseCommentInput: {
            fileNames,
            taskId,
          },
        },
      });

      const urlObject: Record<string, string> | undefined =
        data?.excGetDownloadSignedUrlForExamCourseComment?.signedUrls;

      if (!urlObject) {
        return content;
      }

      return updateImageTags(content, urlObject);
    },
    [taskId, getDownloadSignedUrlForQuestion],
  );

  return { parseContentForDisplay };
}
